/**
 * Tabs.
 *
 * Roving focus with arrow keys, per the WAI-ARIA pattern: `Tab` moves *out* of the tab list, arrows
 * move *between* tabs. Getting this wrong makes a five-tab screen take five tab presses to leave,
 * which is the kind of thing that only shows up when someone actually uses a keyboard all day —
 * which, in a branch, they do.
 *
 * Arrow direction follows the document direction, so `ArrowRight` moves the way the eye does.
 */

import { useRef, type KeyboardEvent } from "react";

import { cx } from "./cx";

export interface Tab {
  id: string;
  label: string;
}

export interface TabsProps {
  tabs: Tab[];
  active: string;
  onChange: (id: string) => void;
  label: string;
  className?: string;
}

export function Tabs({ tabs, active, onChange, label, className }: TabsProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((tab) => tab.id === active);
    if (index < 0) return;

    const rtl = (listRef.current?.closest("[dir]") as HTMLElement | null)?.dir === "rtl";
    const forward = rtl ? "ArrowLeft" : "ArrowRight";
    const back = rtl ? "ArrowRight" : "ArrowLeft";

    let next: number | null = null;
    if (event.key === forward) next = (index + 1) % tabs.length;
    else if (event.key === back) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;

    if (next !== null) {
      event.preventDefault();
      const target = tabs[next];
      if (target) {
        onChange(target.id);
        listRef.current
          ?.querySelector<HTMLButtonElement>(`[data-tab="${target.id}"]`)
          ?.focus();
      }
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cx("flex gap-1 border-b border-borderSubtle", className)}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            data-tab={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            // Only the selected tab is in the tab order — that is what roving focus means.
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cx(
              "rounded-t-sm px-4 py-2 text-body-md",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
              selected
                ? "border-b-2 border-primary font-medium text-textPrimary"
                : "text-textMuted hover:bg-surfaceSunken",
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
